import { ref, uploadBytes, getDownloadURL, uploadBytesResumable, getStorage } from 'firebase/storage';
import { storage } from '../firebase';
import { getAuth } from 'firebase/auth';

const MAX_FILE_SIZE_MB = 50; // 50MB max for videos
const ACCEPTED_TYPES = ['video/mp4', 'video/mov', 'video/avi', 'video/quicktime', 'video/webm'];
const MAX_DURATION_SECONDS = 300; // 5 minutes max

const FIREBASE_STORAGE_ERRORS = {
  'storage/unauthorized': 'Permission denied. Please check if you are signed in.',
  'storage/canceled': 'Upload was cancelled.',
  'storage/unknown': 'An unknown error occurred.',
  'storage/quota-exceeded': 'Storage quota exceeded.',
};

export const validateVideo = (file) => {
  const errors = [];

  // Check file type
  if (!ACCEPTED_TYPES.includes(file.type)) {
    errors.push('Please upload an MP4, MOV, AVI, or WebM video.');
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    errors.push(`File size must be less than ${MAX_FILE_SIZE_MB}MB.`);
  }

  return errors;
};

export const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Could not load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
};

export const uploadVideo = async ({
  file,
  userId,
  path,
  onProgress = () => {},
  onError = () => {},
  onSuccess = () => {}
}) => {
  try {
    const auth = getAuth();
    const storage = getStorage();
    
    // Ensure we have auth
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to upload videos');
    }

    console.log('Starting video upload process...', { file, userId, path, authUser: auth.currentUser.uid });
    
    // Validate video
    const validationErrors = validateVideo(file);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join(' '));
    }

    // Check video duration
    try {
      const duration = await getVideoDuration(file);
      if (duration > MAX_DURATION_SECONDS) {
        throw new Error(`Video must be shorter than ${MAX_DURATION_SECONDS / 60} minutes.`);
      }
    } catch (error) {
      console.warn('Could not check video duration:', error);
      // Continue with upload even if duration check fails
    }
    
    // Use the original file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const timestamp = Date.now();
    const filename = `${timestamp}.${fileExtension}`;
    
    // Use the provided path directly
    const fullPath = path.endsWith('videoURL') ? `${path}/${filename}` : path;
    console.log('Upload path:', fullPath);

    // Create storage reference
    const storageRef = ref(storage, fullPath.replace(/%2F/g, '/'));

    // Create metadata
    const metadata = {
      contentType: file.type,
      cacheControl: 'public,max-age=7200',
      customMetadata: {
        userId: auth.currentUser.uid,
        originalName: file.name,
        uploadedAt: new Date().toISOString()
      }
    };

    // Start upload with metadata
    console.log('Starting video upload to Firebase Storage...');
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          console.log('Upload progress:', progress);
          onProgress(progress);
        },
        // Error handler
        (error) => {
          console.error('Upload error:', error);
          const errorMessage = FIREBASE_STORAGE_ERRORS[error.code] || error.message;
          onError(errorMessage);
          reject(new Error(errorMessage));
        },
        // Completion handler
        async () => {
          try {
            console.log('Upload completed, getting download URL...');
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            const videoData = {
              url: downloadURL,
              path: uploadTask.snapshot.ref.fullPath,
              uploadedAt: new Date().toISOString(),
              size: uploadTask.snapshot.totalBytes,
              contentType: file.type
            };

            console.log('Video upload successful:', videoData);
            onSuccess(downloadURL);
            resolve(videoData);
          } catch (error) {
            console.error('Error getting download URL:', error);
            onError(error.message);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Video upload error:', error);
    onError(error.message);
    throw error;
  }
};

// Add this new function specifically for story videos
export const uploadStoryVideo = async ({
  file,
  storyId,
  onProgress = () => {},
  onError = () => {},
  onSuccess = () => {}
}) => {
  try {
    const auth = getAuth();
    
    // Validate inputs first
    if (!file) {
      throw new Error('No file provided');
    }

    if (!storyId) {
      throw new Error('Missing storyId');
    }

    // Generate filename - use original extension, compression function will convert to .mp4
    const timestamp = Date.now();
    const originalExtension = file.name.split('.').pop().toLowerCase();
    const filename = `${timestamp}.${originalExtension}`;
    
    // Construct the full path for story videos
    const fullPath = `stories/${storyId}/videoURL/${filename}`;
    
    console.log('Uploading story video to path:', fullPath);

    // Get storage reference
    const storage = getStorage();
    const storageRef = ref(storage, fullPath);

    // Start upload
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        // Progress handler
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        },
        // Error handler
        (error) => {
          console.error('Upload error:', error);
          onError(error.message);
          reject(error);
        },
        // Success handler
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('Story video uploaded successfully:', downloadURL);
            onSuccess(downloadURL, fullPath);
            resolve({ url: downloadURL, path: fullPath });
          } catch (error) {
            console.error('Error getting download URL:', error);
            onError(error.message);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    console.error('Story video upload error:', error);
    onError(error.message);
    throw error;
  }
}; 